'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

interface Campaign {
  id: string;
  brand: string;
  product: string;
  category: string;
  reward_amount: number;
}

interface CampaignApplyModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign | null;
}

const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'Beginner', desc: '0–5 reviews done' },
  { value: 'intermediate', label: 'Intermediate', desc: '6–20 reviews done' },
  { value: 'experienced', label: 'Experienced', desc: '21–50 reviews done' },
  { value: 'expert', label: 'Expert', desc: '50+ reviews done' },
];

const CATEGORY_OPTIONS = [
  'Electronics',
  'Fashion & Apparel',
  'Beauty & Personal Care',
  'Home & Kitchen',
  'Sports & Fitness',
  'Books & Stationery',
  'Toys & Games',
  'Automotive',
  'Health & Wellness',
  'Food & Beverages',
  'Jewellery & Accessories',
  'Other',
];

export default function CampaignApplyModal({ isOpen, onClose, campaign }: CampaignApplyModalProps) {
  const router = useRouter();
  const supabase = createClient();

  const [step, setStep] = useState<'form' | 'success' | 'login'>('form');
  const [experienceLevel, setExperienceLevel] = useState('beginner');
  const [categoryMatch, setCategoryMatch] = useState('');
  const [coverNote, setCoverNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(false);

  // Pre-fill category from campaign
  useEffect(() => {
    if (campaign?.category) {
      setCategoryMatch(campaign.category);
    }
  }, [campaign]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setError('');
      setAlreadyApplied(false);
      setSubmitting(false);
      setCoverNote('');
      setExperienceLevel('beginner');
      if (campaign?.category) setCategoryMatch(campaign.category);
    }
  }, [isOpen, campaign]);

  // Check if already applied when modal opens
  useEffect(() => {
    if (!isOpen || !campaign) return;
    async function checkExisting() {
      setCheckingAuth(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setCheckingAuth(false);
          return;
        }
        const { data } = await supabase
          .from('campaign_applications')
          .select('id, application_status')
          .eq('campaign_id', campaign!.id)
          .eq('reviewer_id', user.id)
          .maybeSingle();
        if (data) setAlreadyApplied(true);
      } catch {
        // ignore
      } finally {
        setCheckingAuth(false);
      }
    }
    checkExisting();
  }, [isOpen, campaign, supabase]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaign) return;
    setError('');
    setSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setStep('login');
        setSubmitting(false);
        return;
      }

      const { error: insertError } = await supabase
        .from('campaign_applications')
        .insert({
          campaign_id: campaign.id,
          reviewer_id: user.id,
          experience_level: experienceLevel,
          category_match: categoryMatch,
          cover_note: coverNote.trim() || null,
          application_status: 'pending',
          applied_at: new Date().toISOString(),
        });

      if (insertError) {
        if (insertError.code === '23505') {
          setAlreadyApplied(true);
          setError('You have already applied to this campaign.');
        } else {
          setError(insertError.message || 'Failed to submit application. Please try again.');
        }
        setSubmitting(false);
        return;
      }

      setStep('success');
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-border">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="font-heading font-bold text-foreground text-lg">Apply to Campaign</h2>
            {campaign && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                {campaign.brand} · {campaign.product}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-muted transition-colors text-muted-foreground"
            aria-label="Close"
          >
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {checkingAuth ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : step === 'login' ? (
            <div className="text-center py-6 space-y-4">
              <div className="w-14 h-14 bg-primary-container rounded-full flex items-center justify-center mx-auto">
                <Icon name="UserCircleIcon" size={28} className="text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-foreground text-base mb-1">Sign in to Apply</h3>
                <p className="text-sm text-muted-foreground">
                  You need a reviewer account to apply to campaigns and earn rewards.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => router.push('/auth')}
                  className="cta-gradient text-white font-semibold py-2.5 rounded-xl hover:opacity-90 transition-opacity text-sm"
                >
                  Sign In / Create Account
                </button>
                <button
                  onClick={onClose}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  Maybe later
                </button>
              </div>
            </div>
          ) : step === 'success' ? (
            <div className="text-center py-6 space-y-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Icon name="CheckCircleIcon" size={28} className="text-green-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-foreground text-base mb-1">Application Submitted!</h3>
                <p className="text-sm text-muted-foreground">
                  Your application for <span className="font-medium text-foreground">{campaign?.product}</span> has been submitted.
                  The brand will review it and get back to you soon.
                </p>
              </div>
              <div className="bg-primary-container rounded-xl p-3 text-left">
                <div className="text-xs text-muted-foreground mb-1">Reward if approved</div>
                <div className="font-heading font-bold text-primary text-xl">
                  ₹{(campaign?.reward_amount || 0).toLocaleString('en-IN')}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => router.push('/reviewer/dashboard')}
                  className="cta-gradient text-white font-semibold py-2.5 rounded-xl hover:opacity-90 transition-opacity text-sm"
                >
                  View My Applications
                </button>
                <button
                  onClick={onClose}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  Continue Browsing
                </button>
              </div>
            </div>
          ) : alreadyApplied ? (
            <div className="text-center py-6 space-y-4">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                <Icon name="ClockIcon" size={28} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-foreground text-base mb-1">Already Applied</h3>
                <p className="text-sm text-muted-foreground">
                  You have already submitted an application for this campaign. Check your dashboard for the status.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => router.push('/reviewer/dashboard')}
                  className="cta-gradient text-white font-semibold py-2.5 rounded-xl hover:opacity-90 transition-opacity text-sm"
                >
                  View My Applications
                </button>
                <button
                  onClick={onClose}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Reward Banner */}
              {campaign && (
                <div className="bg-primary-container rounded-xl p-3 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground font-medium">Review Reward</span>
                  <span className="font-heading font-bold text-primary text-lg">
                    ₹{campaign.reward_amount.toLocaleString('en-IN')}
                  </span>
                </div>
              )}

              {/* Experience Level */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Your Experience Level <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {EXPERIENCE_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setExperienceLevel(level.value)}
                      className={`text-left p-3 rounded-xl border transition-all ${
                        experienceLevel === level.value
                          ? 'border-primary bg-primary-container' :'border-border hover:border-primary/50 hover:bg-muted'
                      }`}
                    >
                      <div className={`text-xs font-semibold ${experienceLevel === level.value ? 'text-primary' : 'text-foreground'}`}>
                        {level.label}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{level.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Category Match */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Your Category Expertise <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  Select the category that best matches your reviewing experience.
                </p>
                <select
                  value={categoryMatch}
                  onChange={(e) => setCategoryMatch(e.target.value)}
                  required
                  className="w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                >
                  <option value="">Select a category…</option>
                  {CATEGORY_OPTIONS.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Cover Note */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Cover Note <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <textarea
                  value={coverNote}
                  onChange={(e) => setCoverNote(e.target.value)}
                  placeholder="Tell the brand why you're a great fit for this campaign — your relevant experience, platforms you use, etc."
                  rows={3}
                  maxLength={500}
                  className="w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                />
                <div className="text-right text-[10px] text-muted-foreground mt-1">
                  {coverNote.length}/500
                </div>
              </div>

              {/* Confirmation notice */}
              <div className="flex items-start gap-2.5 bg-muted rounded-xl p-3">
                <Icon name="InformationCircleIcon" size={16} className="text-primary shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  By applying, you confirm you will purchase the product, write an honest review, and follow the campaign guidelines. Reward is paid after review approval.
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                  <Icon name="ExclamationCircleIcon" size={16} className="text-red-500 shrink-0" />
                  <p className="text-xs text-red-600">{error}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 border border-border text-foreground font-semibold py-2.5 rounded-xl hover:bg-muted transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !categoryMatch}
                  className="flex-1 cta-gradient text-white font-semibold py-2.5 rounded-xl hover:opacity-90 transition-opacity text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting…
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
